/**
 * Shared cached materials for procedural aircraft.
 * Singletons to avoid GPU waste when rendering many procedural aircraft.
 */

import * as THREE from 'three'

let _bodyMaterial: THREE.MeshStandardMaterial | null = null
let _accentMaterial: THREE.MeshStandardMaterial | null = null
let _propDiscMaterial: THREE.MeshStandardMaterial | null = null

/** Light white-gray — clean aircraft fuselage/wing paint */
export function getBodyMaterial(): THREE.MeshStandardMaterial {
  if (!_bodyMaterial) {
    _bodyMaterial = new THREE.MeshStandardMaterial({
      color: 0xf0f0f0,
      roughness: 0.4,
      metalness: 0.1,
    })
  }
  return _bodyMaterial
}

/** Darker gray — engines, landing gear, accents */
export function getAccentMaterial(): THREE.MeshStandardMaterial {
  if (!_accentMaterial) {
    _accentMaterial = new THREE.MeshStandardMaterial({
      color: 0x666666,
      roughness: 0.6,
      metalness: 0.2,
    })
  }
  return _accentMaterial
}

/** Translucent disc — spinning propeller representation */
export function getPropDiscMaterial(): THREE.MeshStandardMaterial {
  if (!_propDiscMaterial) {
    _propDiscMaterial = new THREE.MeshStandardMaterial({
      color: 0x999999,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
    })
  }
  return _propDiscMaterial
}

// ========================================
// Ghost Materials — transparent variants for non-present aircraft
// ========================================

let _ghostAwayBody: THREE.MeshStandardMaterial | null = null
let _ghostAwayAccent: THREE.MeshStandardMaterial | null = null
let _ghostIncomingBody: THREE.MeshStandardMaterial | null = null
let _ghostIncomingAccent: THREE.MeshStandardMaterial | null = null
let _ghostReturningBody: THREE.MeshStandardMaterial | null = null
let _ghostReturningAccent: THREE.MeshStandardMaterial | null = null

/** Ghost (away) — semi-transparent gray for based aircraft that have departed */
export function getGhostAwayBodyMaterial(): THREE.MeshStandardMaterial {
  if (!_ghostAwayBody) {
    _ghostAwayBody = new THREE.MeshStandardMaterial({
      color: 0xd0d0d0,
      roughness: 0.5,
      metalness: 0.05,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
  }
  return _ghostAwayBody
}

export function getGhostAwayAccentMaterial(): THREE.MeshStandardMaterial {
  if (!_ghostAwayAccent) {
    _ghostAwayAccent = new THREE.MeshStandardMaterial({
      color: 0x888888,
      roughness: 0.6,
      metalness: 0.1,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
  }
  return _ghostAwayAccent
}

/** Ghost (incoming) — blue-tinted transparent for reserved/expected aircraft */
export function getGhostIncomingBodyMaterial(): THREE.MeshStandardMaterial {
  if (!_ghostIncomingBody) {
    _ghostIncomingBody = new THREE.MeshStandardMaterial({
      color: 0xb0c4de,
      roughness: 0.5,
      metalness: 0.05,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
  }
  return _ghostIncomingBody
}

export function getGhostIncomingAccentMaterial(): THREE.MeshStandardMaterial {
  if (!_ghostIncomingAccent) {
    _ghostIncomingAccent = new THREE.MeshStandardMaterial({
      color: 0x7090b0,
      roughness: 0.6,
      metalness: 0.1,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
  }
  return _ghostIncomingAccent
}

/** Ghost (returning) — green-tinted transparent for inbound aircraft */
export function getGhostReturningBodyMaterial(): THREE.MeshStandardMaterial {
  if (!_ghostReturningBody) {
    _ghostReturningBody = new THREE.MeshStandardMaterial({
      color: 0xb0deb0,
      roughness: 0.5,
      metalness: 0.05,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
  }
  return _ghostReturningBody
}

export function getGhostReturningAccentMaterial(): THREE.MeshStandardMaterial {
  if (!_ghostReturningAccent) {
    _ghostReturningAccent = new THREE.MeshStandardMaterial({
      color: 0x70b070,
      roughness: 0.6,
      metalness: 0.1,
      transparent: true,
      opacity: 0.2,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
  }
  return _ghostReturningAccent
}

/** Dispose all cached materials (call on cleanup) */
export function disposeProceduralMaterials(): void {
  _bodyMaterial?.dispose()
  _accentMaterial?.dispose()
  _propDiscMaterial?.dispose()
  _bodyMaterial = null
  _accentMaterial = null
  _propDiscMaterial = null

  _ghostAwayBody?.dispose()
  _ghostAwayAccent?.dispose()
  _ghostIncomingBody?.dispose()
  _ghostIncomingAccent?.dispose()
  _ghostReturningBody?.dispose()
  _ghostReturningAccent?.dispose()
  _ghostAwayBody = null
  _ghostAwayAccent = null
  _ghostIncomingBody = null
  _ghostIncomingAccent = null
  _ghostReturningBody = null
  _ghostReturningAccent = null
}
